import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Flex, Input, Row, Col, notification } from "antd";
import { AxiosError } from "axios";
import { useForm } from "antd/es/form/Form";
import { DatePicker } from "antd";
import { RequestAtc } from "../redux/apiSlice/requestSlice";
import dayjs from "dayjs";
import {
  approveVoucher,
  ApproveVoucher,
} from "../redux/apiActions/requestsAction";
import { useAppDispatch } from "../redux/hooks";
import axiosClient from "../api/axiosClient";

type Props = {
  record: RequestAtc;
  popup: any;
};

const RequestConfirmForm: React.FC<Props> = ({ record, popup }: Props) => {
  const [form] = useForm();
  const dispatch = useAppDispatch();
  const [otpRequested, setOtpRequested] = useState(false);
  // The request id we have already asked an OTP for. `record` is a fresh object
  // on every table refetch, so keying off the id keeps a re-render from firing a
  // second /otp call and tripping the backend's one-per-minute limit.
  const otpRequestedFor = useRef<string | null>(null);

  const onFinish = async (values: ApproveVoucher) => {
    if (!otpRequested) return;
    const result = await dispatch(
      approveVoucher({ ...values, request_id: record?.id })
    );
    if (approveVoucher.rejected.match(result)) {
      // Wrong/expired OTP — keep the form open so the proctor can retype it.
      // The rejected reducer has already shown the reason.
      form.setFieldValue("otp", "");
      return;
    }
    popup.close();
  };

  useEffect(() => {
    if (!record?.id) return;
    if (otpRequestedFor.current === record.id) return;
    otpRequestedFor.current = record.id;
    axiosClient
      .post(`requests/${record.id}/otp`)
      .then(() => {
        setOtpRequested(true);
      })
      .catch((error: AxiosError<{ status?: string }>) => {
        if (error.response?.status === 429) {
          // An OTP was already sent in the last minute and is still valid — let
          // the user enter it instead of blocking the form. Don't surface the
          // backend's rate-limit wording here: nothing the proctor did was
          // wrong, and it reads as a rejection of the OTP they just typed.
          setOtpRequested(true);
          notification.info({
            message: "An OTP was already sent. Please check your email.",
          });
        } else {
          otpRequestedFor.current = null;
          notification.error({
            message:
              error.response?.data?.status ??
              "Unable to send OTP. Please try again.",
          });
        }
      });
  }, [record?.id]);

  return (
    <>
      <Form
        layout="vertical"
        form={form}
        initialValues={record}
        onFinish={onFinish}
      >
        <Row justify="center" style={{ marginTop: "30px" }}>
          <Col span={12}>
            <Form.Item
              label="Expiry Date"
              name="expiry_date"
              rules={[{ required: true, message: "Expiry Date is required" }]}
              getValueProps={(value) => ({
                value: value ? dayjs(value * 1000) : null,
              })}
              getValueFromEvent={(value) => (value ? value.unix() : null)}
            >
              <DatePicker
                minDate={dayjs()}
                placeholder="DD/MM/YYYY"
                className="full-width"
              />
            </Form.Item>

            <Form.Item
              label="OTP"
              name="otp"
              rules={[{ required: true, message: "OTP is required" }]}
            >
              <Input />
            </Form.Item>

            <Flex justify={"center"}>
              <Form.Item>
                <Button htmlType="submit">Verify</Button>
              </Form.Item>
            </Flex>
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default RequestConfirmForm;
